import { forwardRef, useEffect, useMemo, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { MDBRow, MDBCol } from "mdbreact";

import "./FeuE4ControlActivities.scss";

import { Button, Label, TextArea } from "abs-ui-components";

import { FeuECommonColumns } from "../../FeuConstants";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import AutoSave from "../../../../../common/AutoSave/AutoSave";
import { AUTOSAVE_SUCCESS_MESSAGE } from "../../../Mrv/Constants";

const FeuE4ControlActivities = forwardRef(({ FeuE4ControlActivitiesData, refetchMonitoringData,vesselMstId }, ref) => {
 
    const { control, getValues, reset, formState } = useFormContext();
    const { isDirty } = formState;
    const lastManualSave = useRef(null);
    const manualSaveTriggered = useRef(false);

    useEffect(() => {
        if (FeuE4ControlActivitiesData) {
            reset(prev => ({ ...prev, e4: FeuE4ControlActivitiesData }));
        }
    }, [FeuE4ControlActivitiesData, reset]);

    AutoSave(ref, [], async () => {
        if (!isDirty) return;
        await handleSave(true);
    });

    const e4Columns = useMemo(() => {
        const base = FeuECommonColumns
            .filter(col => col.name !== "versionOfProcedure")
            .map(col => {
                if (col.name === "referenceToProcedure") {
                    return {
                        ...col,
                        label: "Reference for procedure",
                        maxLength: 1000,
                    };
                }
                if (col.name === "responsiblePersonOrPosition") {
                    return {
                        ...col,
                        label: "Name of person or position responsible for data maintenance",
                    };
                }
                if (col.name === "itSystemName") {
                    return {
                        label: "Name of system used (where applicable)",
                        name: "systemName",
                        maxLength: 1000,
                    };
                }
                return col;
            });

        return [
            ...base,
            {
                label: "List of relevant existing management systems",
                name: "managementSystemsList",
                maxLength: 1000,
            },
        ];
    }, []);

    const handleSave = async (isAuto = false) => {
        const formValues = getValues("e4");
        const payload = {
            ...e4Columns.reduce((acc, { name }) => {
                acc[name] = formValues?.[name] || "";
                return acc;
            }, {}),
        };
        try {
            const FeuE4ControlActivities = await callApi({
                method: "POST",
                url: API_ROUTES.E4(vesselMstId),
                data: payload,
            });
            if (FeuE4ControlActivities?.isSuccess) {
                emitToasterMsg("success", "Table E.4", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, e4: formValues }));
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table E.4", FeuE4ControlActivities?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table E.4", error?.errorMessage);
        }
    };

    return (
        <div className="e4-section-form mt-4">
            <MDBRow className="mb-2">
                <MDBCol md="4">
                    <h6 className="titles-font">Title of procedure</h6>
                </MDBCol>
                <MDBCol md="5">
                    <h6 className="titles-font">
                        Information Technology Management (e.g. access controls, back up, recovery and security)
                    </h6>
                </MDBCol>
            </MDBRow>

            {e4Columns.map(({ label, name, maxLength, type }, index) => (
                <MDBRow className="align-items-center mb-3" key={index}>
                    <MDBCol md="4">
                        <Label name={label} bold />
                    </MDBCol>
                    <MDBCol md="4">
                        <Controller
                            name={`e4.${name}`}
                            control={control}
                            render={({ field }) => (
                                <TextArea
                                    id={`e4_${name}`}
                                    className="textarea-resize"
                                    value={field.value || ""}
                                    onChange={field.onChange}
                                    placeholder=""
                                    maxLength={maxLength}
                                    rows={1}
                                />
                            )}
                        />
                    </MDBCol>
                </MDBRow>
            ))}

            <div className="text-center">
                <Button id="feue4_save_btn"
                    disabled={!isDirty}
                    onClick={async () => {
                        manualSaveTriggered.current = true;
                        await handleSave(false);
                    }}
                >
                    Save
                </Button>
            </div>
        </div>
    );
});

export default FeuE4ControlActivities;
